import { Radio } from 'lucide-react';
import renderChannelTypeIcon from 'components/ChannelPicker/renderChannelTypeIcon';
import {
    GREEN_600, PURPLE_500, RED_500,
} from '../../../../../constants';
import getBadgeVariantForHealthStatus from '../../BusinessHealthWidget/helpers/getBadgeVariantForHealthStatus';
import { StyledBadge } from '../../BusinessHealthWidget/styled';
import getLabelForStatus from '../../helpers/getLabelForStatus';
import getChannelWinRateVsBaselineStatus from '../../helpers/getChannelWinRateVsBaselineStatus';
import pickChannelMetricRow from '../../helpers/pickChannelMetricRow';
import { BusinessHealthChannel, BusinessHealthResponse } from '../../types';
import ChannelOpportunitiesPie from './ChannelOpportunitiesPie';
import DrawerMetricCard from './DrawerMetricCard';
import {
    ChannelDrawerHeadingIconWrap,
    ChannelDrawerHeadingRow,
    ChannelDrawerHeadingTitle,
    ChannelDrawerPieWrap,
    ChannelDrawerSection,
    ChannelDrawerTilesGrid,
} from './styled';
import getLabels from '../../BusinessHealthWidget/helpers/getLabels';

const PIE_SLICES = [
    {
        id: 'new',
        label: 'New',
        keys: ['new_opportunities'],
        color: PURPLE_500,
    },
    {
        id: 'won',
        label: 'Won',
        keys: ['opportunities_won'],
        color: GREEN_600,
    },
    {
        id: 'lost',
        label: 'Lost',
        keys: ['opportunities_lost'],
        color: RED_500,
    },
] as const;

const CHANNEL_TILE_METRIC_KEYS = [
    'total_revenue',
    'touch_points',
    'new_opportunities',
    'opportunities_won',
    'win_rate',
] as const;

export interface InsightChannelDrawerSectionProps {
    channel: BusinessHealthChannel;
    data?: BusinessHealthResponse;
}

const InsightChannelDrawerSection = ({ channel, data }: InsightChannelDrawerSectionProps) => {
    const winRateStatus = getChannelWinRateVsBaselineStatus(channel);
    const channelIcon = renderChannelTypeIcon(channel.channel) ?? <Radio aria-hidden />;

    const pieData = PIE_SLICES.map(({
        id, label, keys, color,
    }) => {
        const row = pickChannelMetricRow(channel.metrics, [...keys]);
        const labels = getLabels(keys[0], data);
        return {
            id,
            label: labels?.label ?? labels?.label_short ?? label,
            value: row?.current ?? 0,
            color,
        };
    });

    return (
        <ChannelDrawerSection>
            <ChannelDrawerHeadingRow>
                <ChannelDrawerHeadingIconWrap>
                    {channelIcon}
                </ChannelDrawerHeadingIconWrap>
                <ChannelDrawerHeadingTitle>{channel.channel.replaceAll('_', ' ')}</ChannelDrawerHeadingTitle>
                {winRateStatus ? (
                    <StyledBadge
                        label={getLabelForStatus(winRateStatus)}
                        variant={getBadgeVariantForHealthStatus(winRateStatus)}
                    />
                ) : null}
            </ChannelDrawerHeadingRow>
            <ChannelDrawerPieWrap>
                <ChannelOpportunitiesPie data={pieData} />
            </ChannelDrawerPieWrap>
            <ChannelDrawerTilesGrid>
                {CHANNEL_TILE_METRIC_KEYS.map((metricKey) => {
                    const row = channel.metrics[metricKey];
                    if (!row || !Number.isFinite(row.current)) return null;
                    return (
                        <DrawerMetricCard
                            key={metricKey}
                            metricKey={metricKey}
                            row={row}
                            variant="tile"
                            labels={getLabels(metricKey, data)}
                        />
                    );
                })}
            </ChannelDrawerTilesGrid>
        </ChannelDrawerSection>
    );
};

export default InsightChannelDrawerSection;
